import {FormEvent, Fragment, useState} from 'react';
import {useMutation} from 'services/apollo';
import {EzPage, EzCard, EzHeading, EzField, EzLink} from '@ezcater/recipe';
import {FlashMessages} from '@ezcater/flash-messages';
import type {ExecutionResult} from '@apollo/react-common';
import gql from 'graphql-tag';

import {RequestMagicLink, RequestAccountCreate} from 'services/graphql';
import {formatPhoneNumber} from 'util/StringUtils';
import {useTranslation} from 'react-i18next';
import queryString from 'query-string';
import {useHistory, RouteComponentProps} from 'react-router-dom';
import {usePageViewTracking} from 'util/tracking';
import {REQUEST_SIGN_IN_LINK} from 'util/tracking/trackingEvents';
import {
  DriverSelfAssignSource,
  SelfAssignSource,
  type MagicLinkRequestMutation,
  type MagicLinkRequestMutationVariables,
  type RequestAccountCreateMutation,
  type RequestAccountCreateMutationVariables,
  type RequestSignInAccountCreateMutation,
  type RequestSignInAccountCreateMutationVariables,
  type RequestSignInMagicLinkCreateMutation,
  type RequestSignInMagicLinkCreateMutationVariables,
} from 'services/graphql/types';
import {useFederationApiEppoFlag} from 'services/eppo';
import Button from 'components/Button';
import {TERMS_URL} from 'paths';
import {ErrorText} from 'styles/global.styles';
import Layout from '../Layout';
import RequestSignInLinkSuccess from './RequestSignInLinkSuccess';

export const AccountCreateMutation = gql(/* GraphQL */ `
  mutation RequestSignInAccountCreateMutation($input: DriverAccountSelfCreateInput!) {
    requestAccountCreate: driverAccountSelfCreate(input: $input) {
      userErrors {
        ... on UserError {
          message
        }
      }
    }
  }
`);

type UseAccountCreateMutationResult =
  | ReturnType<
      typeof useMutation<RequestAccountCreateMutation, RequestAccountCreateMutationVariables>
    >
  | ReturnType<
      typeof useMutation<
        RequestSignInAccountCreateMutation,
        RequestSignInAccountCreateMutationVariables
      >
    >;

const useAccountCreateMutation = (): UseAccountCreateMutationResult => {
  const featureFlag = useFederationApiEppoFlag();
  const [executeLegacyMutation, legacyMutationResult] = useMutation<
    RequestAccountCreateMutation,
    RequestAccountCreateMutationVariables
  >(RequestAccountCreate);
  const [executeMutation, mutationResult] = useMutation<
    RequestSignInAccountCreateMutation,
    RequestSignInAccountCreateMutationVariables
  >(AccountCreateMutation);

  if (featureFlag) {
    return [executeMutation, mutationResult];
  }

  return [executeLegacyMutation, legacyMutationResult];
};

export const MagicLinkCreateMutation = gql(/* GraphQL */ `
  mutation RequestSignInMagicLinkCreateMutation($input: DriverAccountMagicLinkCreateInput!) {
    magicLinkRequest: driverAccountMagicLinkCreate(input: $input) {
      userErrors {
        ... on UserError {
          message
        }
      }
    }
  }
`);

const getAdaptedSelfAssignSource = (
  source?: MagicLinkRequestMutationVariables['input']['source']
): DriverSelfAssignSource | undefined => {
  switch (source) {
    case SelfAssignSource.Qrcode: {
      return DriverSelfAssignSource.Qrcode;
    }
    case SelfAssignSource.Sms: {
      return DriverSelfAssignSource.Sms;
    }
    default:
      return undefined;
  }
};

type UseMagicLinkCreateMutationResult =
  | ReturnType<typeof useMutation<MagicLinkRequestMutation, MagicLinkRequestMutationVariables>>
  | [
      (variables: {
        variables: MagicLinkRequestMutationVariables;
      }) => Promise<ExecutionResult<RequestSignInMagicLinkCreateMutation>>,
      ReturnType<
        typeof useMutation<
          RequestSignInMagicLinkCreateMutation,
          RequestSignInMagicLinkCreateMutationVariables
        >
      >[1],
    ];

const useMagicLinkCreateMutation = (): UseMagicLinkCreateMutationResult => {
  const featureFlag = useFederationApiEppoFlag();
  const [executeLegacyMutation, legacyMutationResult] = useMutation<
    MagicLinkRequestMutation,
    MagicLinkRequestMutationVariables
  >(RequestMagicLink);
  const [executeMutation, mutationResult] = useMutation<
    RequestSignInMagicLinkCreateMutation,
    RequestSignInMagicLinkCreateMutationVariables
  >(MagicLinkCreateMutation);

  if (featureFlag) {
    return [
      ({variables}: {variables: MagicLinkRequestMutationVariables}) =>
        executeMutation({
          variables: {
            input: {
              ...variables.input,
              source: getAdaptedSelfAssignSource(variables.input.source),
            },
          },
        }),
      mutationResult,
    ];
  }

  return [executeLegacyMutation, legacyMutationResult];
};

const getUrlParams = (
  location: RouteComponentProps['location']
): {deliveryId?: string; source?: `${SelfAssignSource}`} => {
  const parsedQueryString = queryString.parse(location.search);

  return {
    deliveryId: parsedQueryString.deliveryId as string | undefined,
    source: parsedQueryString.source as SelfAssignSource | undefined,
  };
};

const RequestSignInLink = () => {
  usePageViewTracking(REQUEST_SIGN_IN_LINK);
  const history = useHistory();
  const {t} = useTranslation();
  const [phoneNumber, setPhoneNumber] = useState('');

  const {deliveryId, source} = getUrlParams(history.location);

  const onChange = (value: string) => {
    setPhoneNumber(value);
  };

  const [requestInvitationQuery, requestInvitationData] = useAccountCreateMutation();

  const [magicLinkRequestQuery, magicLinkRequestData] = useMagicLinkCreateMutation();

  const requestInvitation = async () => {
    await requestInvitationQuery({
      variables: {
        input: {
          phone: phoneNumber,
          deliveryId,
        },
      },
    });
  };

  const requestMagicLink = async () => {
    await magicLinkRequestQuery({
      variables: {
        input: {
          phone: phoneNumber,
          deliveryId,
          source,
        },
      },
    });
  };

  const isQrCodeSelfAssign = !!(source && source === SelfAssignSource.Qrcode && deliveryId);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      if (isQrCodeSelfAssign) {
        await requestInvitation();
      } else {
        await requestMagicLink();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const defaultError = t('requestSignInLink.error');
  const title = t('requestSignInLink.title');
  const header = t('requestSignInLink.header');
  const subheader = t('requestSignInLink.subheader');
  const phoneNumberText = t('requestSignInLink.phoneNumber');
  const phoneNumberPlaceholder = t('requestSignInLink.phoneNumberPlaceholder');
  const buttonText = t('requestSignInLink.button');
  const disclaimer = t('requestSignInLink.disclaimer');
  const terms = t('requestSignInLink.terms');

  const requestInvitationUserErrors = requestInvitationData?.data?.requestAccountCreate?.userErrors;
  const requestMagicLinkUserErrors = magicLinkRequestData?.data?.magicLinkRequest?.userErrors;
  const userErrors = requestInvitationUserErrors || requestMagicLinkUserErrors;
  const hasErrors =
    !!requestInvitationData.error || !!magicLinkRequestData.error || !!userErrors?.length;

  const isLoading = requestInvitationData.loading || magicLinkRequestData.loading;
  // @TODO: When removing legacy Driver API, remove `phone` check from
  // conditional logic.
  const requestComplete =
    !isLoading &&
    (!!requestInvitationData?.data?.requestAccountCreate ||
      !!magicLinkRequestData?.data?.magicLinkRequest) &&
    (!userErrors?.length ||
      (requestInvitationData?.data?.requestAccountCreate &&
        'phone' in requestInvitationData.data.requestAccountCreate &&
        !!requestInvitationData.data?.requestAccountCreate?.phone));

  const showSuccessMessages = requestComplete && !hasErrors;

  return (
    <Layout title={title}>
      <EzPage>
        <FlashMessages />
        {showSuccessMessages && <RequestSignInLinkSuccess />}
        {!showSuccessMessages && (
          <>
            <EzHeading size={'3'}>{header}</EzHeading>
            <EzHeading size={'4'}>{subheader}</EzHeading>
            <form onSubmit={handleSubmit}>
              <EzCard>
                {(requestInvitationData.error || magicLinkRequestData.error) && (
                  <ErrorText>{defaultError}</ErrorText>
                )}
                {!!userErrors?.length && (
                  <ErrorText>
                    {userErrors.map((error, i) => (
                      <Fragment key={i}>
                        {error.message}
                        <br />
                      </Fragment>
                    ))}
                  </ErrorText>
                )}
                <>
                  <EzField
                    type="text"
                    label={phoneNumberText}
                    value={formatPhoneNumber(phoneNumber)}
                    onChange={e => onChange(e.currentTarget.value)}
                    placeholder={phoneNumberPlaceholder}
                  />
                  <Button
                    type="submit"
                    buttonType="primary"
                    buttonText={buttonText}
                    loading={isLoading}
                    disabled={phoneNumber === ''}
                  />
                  <EzHeading size="6">
                    {disclaimer}&nbsp;&nbsp;
                    <EzLink href={TERMS_URL} target="_blank">
                      {terms}
                    </EzLink>
                  </EzHeading>
                </>
              </EzCard>
            </form>
          </>
        )}
      </EzPage>
    </Layout>
  );
};

export default RequestSignInLink;
